import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocalizedStrings } from 'app/localization/Context';
import { CurrentPageContext, HistoryStackContext } from 'app/home/general-components/HistoryStackContext';
import { AudioFilesFetchStatus } from 'app/model/AudioFilesController';
import CustomIconBack from 'app/home/home-components/CustomIconBack';

import { ReactComponent as IconPlayDark } from 'images/device/audio-controller/play-dark.svg';
import { ReactComponent as IconLoading } from 'images/general/clone-of-connecting-white.svg';
import { ReactComponent as IconX } from 'images/general/x.svg';
import { ReactComponent as IconRefresh } from 'images/general/refresh-white.svg';

import './SelectMusicList.scss'

export function SelectMusicList({ audioFilesData, audioFilesController }) {
    const s = useLocalizedStrings();
    const a = s.audioController;

    const hs = useContext(HistoryStackContext);
    const p = useContext(CurrentPageContext);
    const deviceMac = p.state.deviceMac;
    const connected = p.state.connected;

    const [folderTabActive, setForderTabActive] = useState(true);

    useEffect(() => {
        if (folderTabActive) {
            if (audioFilesData.foldersFetchStatus === AudioFilesFetchStatus.nothing) {
                audioFilesController.fetchFolders();
            }
        } else {
            if (audioFilesData.albumsFetchStatus === AudioFilesFetchStatus.nothing) {
                audioFilesController.fetchAlbums();
            }
        }
    }, [folderTabActive, audioFilesData, audioFilesController]);

    const [dataIsLoading, dataList] = useMemo(() => {
        let l = [];
        if (folderTabActive) {
            if (audioFilesData.folders !== undefined) {
                l = audioFilesData.folders;
            }
            return [audioFilesData.foldersFetchStatus !== AudioFilesFetchStatus.fetched, l];
        } else {
            if (audioFilesData.albums !== undefined) {
                l = audioFilesData.albums;
            }
            return [audioFilesData.albumsFetchStatus !== AudioFilesFetchStatus.fetched, l];
        }
    }, [folderTabActive, audioFilesData]);

    return (
        <div className="select-music-list-page">
            <div className="bg-blank" />
            <div className={connected ? "bg-secondary-color" : "bg-disconnect-color"} />
            <div className="select-music-list-page-content">
                <div className="header-group">
                    <CustomIconBack className="back-icon" />
                    <div />
                    <div className="button-border" onClick={audioFilesController.removeAllCaches}>
                        <div className="label">{a.reload}</div>
                        <IconRefresh className="icon" />
                    </div>
                </div>
                <div className="nav-button" onClick={p.dismiss} />
                <div className="page-header">{a.chooseMusicList}</div>

                <div className="two-tabs-header-container">
                    <div className="tabs-container">
                        <div className={folderTabActive ? "tab active" : "tab"} onClick={() => { setForderTabActive(true) }}>
                            <div className="name">{a.folders}</div>
                            <div className="bar" />
                        </div>
                        <div className={!folderTabActive ? "tab active" : "tab"} onClick={() => { setForderTabActive(false) }}>
                            <div className="name">{a.albums}</div>
                            <div className="bar" />
                        </div>

                        <div className="full-bar" />
                    </div>
                </div>

                {dataIsLoading ? <div className="info">
                    <IconLoading className="icon" />
                    <div className="text">{a.loadingData}</div>
                </div> : null}

                {(!dataIsLoading && (dataList.length === 0)) ? <div className="info">
                    <IconX className="icon" />
                    <div className="text">{a.noMusicFound}</div>
                </div> : null}

                {(folderTabActive && !dataIsLoading && (dataList.length > 0)) ? <div className="foldes-tab">
                    {dataList.map((elem, index) =>
                        <div className={connected ? "row" : "row transparent"}
                            key={"music-list-foldes-tab-row-" + String(index)}
                            onClick={() => {
                                hs.push(
                                    "/devices/detail/select-music-list/show-list",
                                    { deviceMac: deviceMac, listType: 1, listPath: elem.path, listName: elem.name, connected })
                            }}>
                            <div className="title">{elem.name}</div>
                            <div className="info">{elem.path}</div>
                            <div className="tracks">{(elem.no_tracks > 1) ?
                                (String(elem.no_tracks) + " " + a.tracks) :
                                (String(elem.no_tracks) + " " + a.track)}</div>
                        </div>)}
                </div> : null}

                {(!folderTabActive && !dataIsLoading && (dataList.length > 0)) ? <div className="albums-tab">
                    {dataList.map((elem, index) =>
                        <div className={connected ? "row" : "row transparent"}
                            key={"music-list-albums-tab-row-" + String(index)}
                            onClick={() => {
                                hs.push(
                                    "/devices/detail/select-music-list/show-list",
                                    { deviceMac: deviceMac, listType: 2, listName: elem.name, connected })
                            }}>
                            <div className="title">{elem.name}</div>
                            <div className="info">{elem.artists}</div>
                            <div className="tracks">{(elem.no_tracks > 1) ?
                                (String(elem.no_tracks) + " " + a.tracks) :
                                (String(elem.no_tracks) + " " + a.track)}</div>
                        </div>)}
                </div> : null}
            </div>
        </div>
    );
}

export function ShowMusicList({ audioFilesData, audioFilesController, devices }) {
    const s = useLocalizedStrings();
    const a = s.audioController;

    const hs = useContext(HistoryStackContext);
    const p = useContext(CurrentPageContext);
    const connected = p.state.connected;
    const deviceMac = p.state.deviceMac;
    const listType = p.state.listType; // 1: folder, 2: album
    const listPath = p.state.listPath; // for folder
    const listName = p.state.listName; // for album, and also for folder

    const onSelect = useCallback(() => {
        const deviceL = devices.filter(d => d.mac === deviceMac);
        if (deviceL.length > 0) {
            deviceL[0].setTrackList({ listType, listName, listPath });
        }

        hs.back(2);
    }, [deviceMac, devices,
        listType, listName, listPath, hs]);

    const listObj = useMemo(() => {
        if (listType === 1) { // folder
            for (let i = 0; i < audioFilesData.folders.length; i++) {
                const folder = audioFilesData.folders[i];
                if (folder.path === listPath) {
                    return folder;
                }
            }
        } else if (listType === 2) { // album
            for (let i = 0; i < audioFilesData.albums.length; i++) {
                const album = audioFilesData.albums[i];
                if (album.name === listName) {
                    return album;
                }
            }
        }
    }, [listType, listPath, listName, audioFilesData]);

    const dataIsLoading = listObj["tracksFetchStatus"] !== AudioFilesFetchStatus.fetched;

    useEffect(() => {
        if (listObj["tracksFetchStatus"] === AudioFilesFetchStatus.nothing) {
            if (listType === 1) { // folder
                audioFilesController.fetchFolderDetail(listPath);
            } else if (listType === 2) { // album
                audioFilesController.fetchAlbumDetail(listName);
            }
        }
    }, [listObj, listType, listPath, listName, audioFilesController]);

    return (
        <div className="select-music-list-page">
            <div className="bg-blank" />
            <div className={connected ? "bg-secondary-color" : "bg-disconnect-color"} />
            <div className="select-music-list-page-content show-list">
                <div className="header-group">
                    <CustomIconBack className="back-icon" />
                </div>
                <div className="nav-button" onClick={p.dismiss} />
                <div className="page-header">{a.listDetailFunc(listName)}</div>

                {dataIsLoading ?
                    <div className="info">
                        <IconLoading className="icon" />
                        <div className="text">{a.loadingData}</div>
                    </div> : null}

                {!dataIsLoading ?
                    <div className="the-rest-of-page-container with-full-button-margin">
                        <div className="tracks">
                            {listObj["tracks"].map((elem, index) =>
                                <Fragment key={"music-list-detail-row-fragment-" + String(index)}>
                                    <div className="row" key={"music-list-detail-row-" + String(index)}>
                                        <div className="index">{formatDigit(index + 1)}</div>
                                        <div className="title">{elem.name}</div>
                                        <div className="artists">{elem.artists}</div>
                                        <div className="duration">{
                                            String(formatDigit(Math.floor(elem.duration / 60))) + ":" +
                                            String(formatDigit(elem.duration % 60))}
                                        </div>
                                    </div>
                                    {index !== (listObj["tracks"].length - 1) ? <div className="bar" key={"music-list-detail-row-bar-" + String(index)} /> : null}
                                </Fragment>
                            )}
                        </div>
                    </div> : null}

                {!dataIsLoading ?
                    <div className="full-blue-button fix-on-bottom" onClick={onSelect}>
                        <div>{a.chooseAndPlay}</div> <IconPlayDark className="icon" />
                    </div> : null}
            </div>
        </div>
    );
}

function formatDigit(d) {
    return d < 10 ? ("0" + String(d)) : String(d);
}
